/**
 * Frontend workflow access policy registry.
 * This is the single source of truth for step order + prerequisites in the UI.
 */

export enum StepKey {
  STEP_1 = '1',
  STEP_2 = '2',
  STEP_3 = '3',
  STEP_4 = '4',
  STEP_5 = '5',
  STEP_6 = '6',
  STEP_7 = '7',
  STEP_8 = '8',
  STEP_9 = '9',
  STEP_10 = '10',
  STEP_11 = '11',
  // Backward-compatible constant for stale payloads. Not part of active workflow policy.
  STEP_12 = '12',
}

export type StepNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
export const WORKFLOW_STEP_ORDER: StepNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
export const WORKFLOW_LAST_STEP: StepNumber = 11;

export interface WorkflowStepPolicy {
  stepNumber: StepNumber;
  name: string;
  prerequisites: StepNumber[];
  phase: 'project-setup' | 'ai-generation' | 'launch';
}

export const WORKFLOW_STEP_POLICIES: Record<StepNumber, WorkflowStepPolicy> = {
  1: { stepNumber: 1, name: 'Client Profile', prerequisites: [], phase: 'project-setup' },
  2: { stepNumber: 2, name: 'Partners', prerequisites: [1], phase: 'project-setup' },
  3: { stepNumber: 3, name: 'Knowledge Base', prerequisites: [1, 2], phase: 'project-setup' },
  4: { stepNumber: 4, name: 'Campaign Setup', prerequisites: [1, 2, 3], phase: 'project-setup' },
  5: { stepNumber: 5, name: 'Strategy', prerequisites: [1, 2, 3, 4], phase: 'ai-generation' },
  6: { stepNumber: 6, name: 'Buying Committee', prerequisites: [1, 2, 3, 4, 5], phase: 'ai-generation' },
  7: { stepNumber: 7, name: 'Buyer Personas', prerequisites: [1, 2, 3, 4, 5, 6], phase: 'ai-generation' },
  8: { stepNumber: 8, name: 'Generate Content', prerequisites: [5, 6, 7], phase: 'ai-generation' },
  9: { stepNumber: 9, name: 'Review & Revise', prerequisites: [5, 6, 7, 8], phase: 'launch' },
  10: { stepNumber: 10, name: 'Channel Guide', prerequisites: [5, 6, 7, 8, 9], phase: 'launch' },
  11: { stepNumber: 11, name: 'Export & Delivery', prerequisites: [5, 6, 7, 8, 9, 10], phase: 'launch' },
};

export const WORKFLOW_PHASES = [
  {
    id: 'project-setup',
    name: 'Campaign Setup',
    steps: [1, 2, 3, 4] as StepNumber[],
    description: 'Client, partners, knowledge, and campaign context',
    icon: '📋',
  },
  {
    id: 'ai-generation',
    name: 'Content Generation',
    steps: [5, 6, 7, 8] as StepNumber[],
    description: 'Strategy, personas, and campaign content creation',
    icon: '✨',
  },
  {
    id: 'launch',
    name: 'Review & Delivery',
    steps: [9, 10, 11] as StepNumber[],
    description: 'Quality checks, channel plan, and export',
    icon: '🚀',
  },
] as const;

export const STEP_NAMES: Record<StepNumber, string> = WORKFLOW_STEP_ORDER.reduce(
  (acc, step) => {
    acc[step] = WORKFLOW_STEP_POLICIES[step].name;
    return acc;
  },
  {} as Record<StepNumber, string>,
);

const LEGACY_STEP_KEYS_BY_NUMBER: Record<number, StepKey | undefined> = {
  1: StepKey.STEP_1,
  2: StepKey.STEP_2,
  3: StepKey.STEP_3,
  4: StepKey.STEP_4,
  5: StepKey.STEP_5,
  6: StepKey.STEP_6,
  7: StepKey.STEP_7,
  8: StepKey.STEP_8,
  9: StepKey.STEP_9,
  10: StepKey.STEP_10,
  11: StepKey.STEP_11,
  12: StepKey.STEP_12,
};

export const isWorkflowStepNumber = (value: number): value is StepNumber =>
  WORKFLOW_STEP_ORDER.includes(value as StepNumber);

const toStepNumber = (value: number): StepNumber | null => (isWorkflowStepNumber(value) ? value : null);

const normalizeCompletedSteps = (
  completedSteps: Set<StepKey | StepNumber> | number[],
): Set<StepNumber> => {
  const normalized = new Set<StepNumber>();
  const source = Array.isArray(completedSteps) ? completedSteps : Array.from(completedSteps.values());

  for (const item of source) {
    const numericValue = Number(item);
    if (isWorkflowStepNumber(numericValue)) {
      normalized.add(numericValue);
    }
  }

  return normalized;
};

/**
 * Check if all prerequisites are met for a target step.
 */
export function validatePrerequisites(
  targetStep: number,
  completedSteps: Set<StepKey | StepNumber> | number[],
): { valid: boolean; missingSteps: StepNumber[] } {
  const normalizedTarget = toStepNumber(targetStep);
  if (!normalizedTarget) {
    return { valid: false, missingSteps: [] };
  }

  const completed = normalizeCompletedSteps(completedSteps);
  const required = WORKFLOW_STEP_POLICIES[normalizedTarget].prerequisites;
  const missing = required.filter((step) => !completed.has(step));
  return {
    valid: missing.length === 0,
    missingSteps: missing,
  };
}

export function getPrerequisiteErrorMessage(
  targetStep: number,
  completedSteps: Set<StepKey | StepNumber> | number[],
): string | null {
  const { valid, missingSteps } = validatePrerequisites(targetStep, completedSteps);
  if (valid) return null;

  const missingNames = missingSteps
    .sort((a, b) => a - b)
    .map((step) => `Step ${step} (${STEP_NAMES[step]})`)
    .join(', ');

  return `Please complete the following steps first: ${missingNames}`;
}

export function canAccessStep(
  stepNumber: number,
  completedSteps: Set<StepKey | StepNumber> | number[],
): boolean {
  return validatePrerequisites(stepNumber, completedSteps).valid;
}

export function getPrerequisites(stepNumber: number): StepNumber[] {
  const normalizedStep = toStepNumber(stepNumber);
  if (!normalizedStep) return [];
  return [...WORKFLOW_STEP_POLICIES[normalizedStep].prerequisites];
}

export function getStepKey(stepNumber: number): StepKey | null {
  return LEGACY_STEP_KEYS_BY_NUMBER[stepNumber] ?? null;
}

export function isAIGenerationStep(stepNumber: number): boolean {
  const normalizedStep = toStepNumber(stepNumber);
  return normalizedStep !== null && WORKFLOW_STEP_POLICIES[normalizedStep].phase === 'ai-generation';
}

export function isUserEditingStep(stepNumber: number): boolean {
  const normalizedStep = toStepNumber(stepNumber);
  return normalizedStep !== null && WORKFLOW_STEP_POLICIES[normalizedStep].phase === 'launch';
}

export function isSetupStep(stepNumber: number): boolean {
  const normalizedStep = toStepNumber(stepNumber);
  return normalizedStep !== null && WORKFLOW_STEP_POLICIES[normalizedStep].phase === 'project-setup';
}

export function getRequiredSteps(stepNumber: number): StepNumber[] {
  return getPrerequisites(stepNumber);
}
